import React from 'react';
import classNames from 'classnames';
import { trackClick, Options as TrackClickOptions } from 'js/utils/trackClick';

import 'app/assets/stylesheets/design_system/heading.scss';

type HeadingSizes = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type HeadingLevels = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export type BaseHeadingProps = {
  fontFamily?: 'serif' | 'sans-serif';
  size?: HeadingSizes;
  as?: HeadingLevels;
  href: string;
  linkProps?: React.ComponentPropsWithoutRef<'a'>;
  external?: boolean;
  trackClick?: TrackClickOptions;
};

type HeadingProps<T extends HeadingLevels = HeadingLevels> = BaseHeadingProps &
  React.ComponentPropsWithoutRef<T>;

const sizeToHeadingTag: Record<HeadingSizes, HeadingLevels> = {
  xs: 'h5',
  sm: 'h4',
  md: 'h3',
  lg: 'h2',
  xl: 'h1',
};

/**
 * HeadingLink component used for section headers, article titles, paper titles, etc. that must also be a link.
 * Available in serif and sans-serif font types and various sizes.
 * Heading sibling component is available when you need just a heading without link behavior.
 *
 * Props:
 * - `fontFamily`: Font type for the heading (default: 'sans-serif')
 *               Choose between 'serif' | 'sans-serif'
 * - `size`: Controls the font-size and line-height of the heading (default: 'xl')
 *           Choose between 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * - `as`: HTML heading level to render as. Without specifying, the component's heading level
 *         is determined by the size (e.g. 'md' will render as <h3>)
 *         Choose between 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
 * - `href`: The URL to link to
 * - `linkProps`: Additional props to pass to the anchor tag
 * - `external`: Whether the link should open in a new tab (default: false)
 * - `trackClick`: Options for tracking clicks on this heading link
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { HeadingLink } from 'js/design_system/link/Heading';
 *
 * <HeadingLink href="#">A Basic Heading Link</HeadingLink>
 * ```
 * ---
 *
 * ## Custom Font Type and Size
 *
 * @example
 *
 * ```tsx
 * import { HeadingLink } from 'js/design_system/link/HeadingLink';
 *
 * <HeadingLink href="#" fontFamily='serif' size='lg'>Large Serif Heading</HeadingLink>
 * ```
 * ---
 *
 * ## Custom Heading Level
 * ```tsx
 * import { HeadingLink } from 'js/design_system/link/HeadingLink';
 *
 * <HeadingLink href="#" as='h3' size="sm">Custom Heading Level</HeadingLink>
 * ```
 * ---
 *
 * ## Passing props to the anchor tag
 *
 * @example
 *
 * ```tsx
 * import { HeadingLink } from 'js/design_system/link/HeadingLink';
 *
 * <HeadingLink href="bad_for_seo.com" linkProps={{ rel: 'nofollow }}>A nofollow heading</HeadingLink>
 * ```
 *
 */
export const HeadingLink = React.forwardRef<HTMLAnchorElement, HeadingProps>(
  (
    {
      fontFamily = 'sans-serif',
      size = 'xl',
      as,
      linkProps,
      className,
      external = false,
      children,
      trackClick: trackClickOptions,
      ...restProps
    },
    ref,
  ) => {
    const HeadingTag = as || sizeToHeadingTag[size];

    const classes = classNames(
      className,
      `ds2-5-heading-${fontFamily}-${size}`,
    );

    const handleClick = trackClickOptions
      ? (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (trackClickOptions) {
            trackClick(trackClickOptions);
          }
          return linkProps?.onClick?.(event);
        }
      : linkProps?.onClick;

    return (
      <HeadingTag className={classes} {...restProps}>
        <a
          {...linkProps}
          target={external ? '_blank' : undefined}
          rel={`${linkProps?.rel} ${external ? 'noreferrer' : undefined}`}
          className={'ds2-5-heading-link'}
          onClick={handleClick}
          ref={ref}
        >
          {children}
        </a>
      </HeadingTag>
    );
  },
);
